import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Label } from '@/components/atoms/label';
import { Input } from '@/components/atoms/input';
import { BannerImageUpload } from '../../upload/BannerImageUpload';
import { BannerActionSelector } from '../../action/BannerActionSelector';
import { CarouselPreview, BannerPreview } from '../../preview/CarouselPreview';
import type { BannerAction, SelectedProduct } from '../../../stores/campaign-wizard.store';
import type { AdType } from '../../../types/ads.types';
import type { AspectRatio } from '../../upload/ImageCropper';

// ============ Types ============

interface StepBannerUploadProps {
  campaignType: AdType;
  bannerImage: File | null;
  bannerImagePreview: string | null;
  onBannerImageChange: (file: File | null, preview: string | null) => void;
  bannerAction: BannerAction;
  onBannerActionChange: (action: BannerAction) => void;
  headline: string;
  onHeadlineChange: (headline: string) => void;
  shopName?: string;
  shopRefId?: string;
  products?: SelectedProduct[];
}

// ============ Helpers ============

function getAspectRatioForType(type: AdType): AspectRatio {
  switch (type) {
    case 'homepage_carousel':
      return '1:1'; // Square for carousel
    case 'category_banner':
      return '16:9'; // Wide for banner
    default:
      return '1:1';
  }
}

function getMinDimensionsForType(type: AdType): { width: number; height: number } {
  switch (type) {
    case 'homepage_carousel':
      return { width: 1080, height: 1080 };
    case 'category_banner':
      return { width: 1920, height: 1080 };
    default:
      return { width: 1080, height: 1080 };
  }
}

// ============ Component ============

export function StepBannerUpload({
  campaignType,
  bannerImage,
  bannerImagePreview,
  onBannerImageChange,
  bannerAction,
  onBannerActionChange,
  headline,
  onHeadlineChange,
  shopName = 'Ma boutique',
  shopRefId: _shopRefId,
  products = [],
}: StepBannerUploadProps) {
  const { t } = useTranslation();
  void _shopRefId; // Available for future use

  const isCarousel = campaignType === 'homepage_carousel';

  const aspectRatio = getAspectRatioForType(campaignType);
  const { width: minWidth, height: minHeight } = getMinDimensionsForType(campaignType);

  // Get selected product for preview
  const selectedProduct = React.useMemo(() => {
    if (bannerAction.type === 'product' && bannerAction.productRefId) {
      return products.find((p) => p.refId === bannerAction.productRefId) || null;
    }
    return null;
  }, [bannerAction, products]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - smaller on mobile */}
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">
          {isCarousel
            ? t('ads.wizard.step2.titleCarousel', 'Image du carousel')
            : t('ads.wizard.step2.titleBanner', 'Image de la bannière')}
        </h2>
        <p className="text-sm text-muted-foreground hidden sm:block">
          {isCarousel
            ? t(
                'ads.wizard.step2.subtitleCarousel',
                "Téléchargez une image attrayante pour votre carousel sur la page d'accueil"
              )
            : t(
                'ads.wizard.step2.subtitleBanner',
                'Téléchargez une image pour votre bannière de catégorie'
              )}
        </p>
      </div>

      {/* Main content grid */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Left: Upload and action */}
        <div className="space-y-4 sm:space-y-6">
          {/* Image upload */}
          <div className="space-y-2 sm:space-y-3">
            <Label className="text-sm">
              {isCarousel
                ? t('ads.wizard.step2.carouselImage', 'Image du carousel')
                : t('ads.wizard.step2.bannerImage', 'Image de la bannière')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <BannerImageUpload
              value={{ file: bannerImage, preview: bannerImagePreview }}
              onChange={onBannerImageChange}
              aspectRatio={aspectRatio}
              minWidth={minWidth}
              minHeight={minHeight}
            />
          </div>

          {/* Action selector */}
          <BannerActionSelector
            value={bannerAction}
            onChange={onBannerActionChange}
            shopName={shopName}
            products={products}
          />

          {/* Headline input */}
          <div className="space-y-2">
            <Label className="text-sm">
              {t('ads.wizard.step2.headline', 'Texte promotionnel')}
              <span className="text-muted-foreground ml-1 font-normal">(optionnel)</span>
            </Label>
            <div className="relative">
              <Input
                value={headline}
                onChange={(e) => onHeadlineChange(e.target.value.slice(0, 40))}
                placeholder={t('ads.wizard.step2.headlinePlaceholder', 'Ex: -50% sur tout le magasin !')}
                maxLength={40}
                className="pr-14"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {headline.length}/40
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('ads.wizard.step2.headlineHint', 'Ce texte apparaîtra en gras sur votre visuel')}
            </p>
          </div>

          {/* Requirement indicator - more compact on mobile */}
          {!bannerImagePreview && (
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 text-sm">
                <Info className="size-4 sm:size-5 shrink-0" />
                <span className="font-medium">
                  Téléchargez une image pour continuer
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right: Preview - hidden on mobile unless image selected */}
        <div className={bannerImagePreview ? 'space-y-2 sm:space-y-3' : 'hidden lg:block space-y-3'}>
          <Label className="text-sm font-medium">Aperçu</Label>
          <div className="bg-muted/30 rounded-xl p-2 sm:p-4 flex items-center justify-center min-h-[250px] sm:min-h-[400px]">
            {isCarousel ? (
              <CarouselPreview
                imageUrl={bannerImagePreview}
                shopName={shopName}
                action={bannerAction}
                selectedProduct={selectedProduct}
                headline={headline}
              />
            ) : (
              <BannerPreview
                imageUrl={bannerImagePreview}
                shopName={shopName}
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Aperçu de l'affichage sur mobile
          </p>
        </div>

      </div>

      {/* Tips - hidden on mobile */}
      <div className="hidden sm:block bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="size-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-100">
              Conseils pour une image efficace
            </p>
            <ul className="text-blue-700 dark:text-blue-300 mt-1 space-y-1 list-disc list-inside">
              <li>Utilisez des couleurs vives et contrastées</li>
              <li>Évitez trop de texte sur l'image</li>
              <li>Mettez en valeur vos produits phares</li>
              <li>
                Format recommandé:{' '}
                {isCarousel ? '1080x1080px (carré)' : '1920x1080px (16:9)'}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile: Format hint */}
      <p className="sm:hidden text-xs text-muted-foreground text-center">
        Format: {isCarousel ? 'carré (1:1)' : '16:9'} • Min {minWidth}x{minHeight}px
      </p>
    </div>
  );
}
